import React, { useState, useEffect } from 'react';
import { Plus, Minus, X, Info } from 'lucide-react';
import axios from 'axios';
import './Varieties.css';
import { useCart } from '../context/CartContext';

const Varieties = () => {
  const { addToCart, removeFromCart, getItemQuantity } = useCart();
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products/?available_only=true`);
        setVarieties(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [API_URL]);

  const openModal = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  // Handle body scroll lock via useEffect
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  if (loading) {
    return (
      <section id="varieties" className="varieties-section">
        <div className="container">
          <div className="loading-state">Loading varieties...</div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="varieties" className="varieties-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Our Microgreens Varieties</h2>
            <p className="body-large section-subtitle">
              Carefully grown and hand-picked for maximum nutrition and flavor
            </p>
            <p className="body-medium section-instruction">
              Click on any variety to learn more • Add to cart for one-time orders
            </p>
          </div>
          <div className="varieties-grid">
            {varieties.map((variety) => {
              const quantity = getItemQuantity(variety.id);
              return (
                <div key={variety.id} className="variety-card">
                  <div className="variety-image-container" onClick={() => openModal(variety)}>
                    <img 
                      src={variety.image} 
                      alt={variety.name}
                      className="variety-image"
                    />
                    <div className="info-overlay">
                      <Info size={24} />
                      <span>Click for details</span>
                    </div>
                  </div>
                  <div className="variety-content">
                    <h3 className="heading-3" onClick={() => openModal(variety)} style={{cursor: 'pointer'}}>
                      {variety.name}
                    </h3>
                    <p className="body-small variety-weight">{variety.quantity}</p>
                    <div className="variety-cart-actions">
                      <span className="variety-price">₹{variety.price}</span>
                      {quantity === 0 ? (
                        <button 
                          className="btn-add-to-cart"
                          onClick={() => addToCart(variety)}
                        >
                          Add to Cart
                        </button>
                      ) : (
                        <div className="quantity-controls">
                          <button 
                            className="qty-btn"
                            onClick={() => removeFromCart(variety.id)}
                          >
                            <Minus size={18} />
                          </button>
                          <span className="quantity-display">{quantity}</span>
                          <button 
                            className="qty-btn"
                            onClick={() => addToCart(variety)}
                          >
                            <Plus size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Details Modal */}
      {showModal && selectedProduct && (
        <div className="product-modal-overlay" onClick={closeModal}>
          <div className="product-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <X size={24} />
            </button>
            
            <div className="modal-content">
              <div className="modal-header">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="modal-image" />
                <div className="modal-title-section">
                  <h2>{selectedProduct.name}</h2>
                  <p className="modal-quantity">{selectedProduct.quantity} • ₹{selectedProduct.price}</p>
                </div>
              </div>

              <div className="modal-body">
                <div className="modal-section">
                  <h3>Description</h3>
                  <p>{selectedProduct.description}</p>
                </div>

                <div className="modal-section">
                  <h3>Key Benefits</h3>
                  <ul>
                    {selectedProduct.keyBenefits.map((benefit, index) => (
                      <li key={`benefit-${benefit}`}>{benefit}</li>
                    ))}
                  </ul>
                </div>

                <div className="modal-section">
                  <h3>Taste Profile</h3>
                  <p>{selectedProduct.tasteProfile}</p>
                </div>

                <div className="modal-section">
                  <h3>Nutritional Highlights</h3>
                  <ul>
                    {selectedProduct.nutritionalHighlights.map((nutrient, index) => (
                      <li key={`nutrient-${nutrient}`}>{nutrient}</li>
                    ))}
                  </ul>
                </div>

                <div className="modal-section">
                  <h3>How to Use</h3>
                  <ul>
                    {selectedProduct.howToUse.map((use, index) => (
                      <li key={`use-${use}`}>{use}</li>
                    ))}
                  </ul>
                </div>

                <div className="modal-section">
                  <h3>Storage & Shelf Life</h3>
                  <p>{selectedProduct.storageInfo}</p>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn-primary btn-add-from-modal"
                  onClick={() => {
                    addToCart(selectedProduct);
                    closeModal();
                  }}
                >
                  Add to Cart - ₹{selectedProduct.price}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Varieties;
